/* eslint-disable jsx-a11y/label-has-associated-control */

import { useEnhancedField } from "@uplift-ltd/formik";
import React, { useCallback } from "react";
import { FormGroup, useFormGroup } from "./FormGroup";
import { HelpText } from "./HelpText";
import { Label } from "./Label";
import { TextArea, TextAreaProps } from "./TextArea";

interface TextAreaGroupProps extends TextAreaProps {
  className?: string;
  label: React.ReactNode;
  helpText?: React.ReactNode;
  onBeforeChange?(e: React.ChangeEvent<HTMLTextAreaElement>): void;
}

export function TextAreaGroup({
  className,
  label,
  helpText,
  id,
  status,
  ...rest
}: TextAreaGroupProps) {
  const { labelProps, inputProps, helpTextProps } = useFormGroup({ id });
  return (
    <FormGroup
      className={className}
      label={
        <Label {...labelProps} required={rest.required}>
          {label}
        </Label>
      }
      input={<TextArea {...rest} {...inputProps} status={status} />}
      helpText={
        <HelpText {...helpTextProps} status={status}>
          {helpText}
        </HelpText>
      }
    />
  );
}

interface TextAreaGroupFieldProps extends TextAreaGroupProps {
  name: string;
  textareaClassName?: string;
  helpTextClassName?: string;
}

export function TextAreaGroupField({
  className,
  name,
  label,
  helpText,
  id,
  textareaClassName,
  helpTextClassName,
  onBeforeChange,
  ...rest
}: TextAreaGroupFieldProps) {
  const [field, meta] = useEnhancedField<string>({ name });
  const status = meta.touched && meta.error ? "danger" : undefined;
  const { labelProps, inputProps, helpTextProps } = useFormGroup({ id });

  const { onChange } = field;

  const combinedOnChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onBeforeChange?.(e);
      onChange(e);
    },
    [onBeforeChange, onChange]
  );

  return (
    <FormGroup
      className={className}
      label={
        <Label {...labelProps} required={rest.required}>
          {label}
        </Label>
      }
      input={
        <TextArea
          className={textareaClassName}
          {...field}
          {...rest}
          {...inputProps}
          onChange={combinedOnChange}
          status={status}
        />
      }
      helpText={
        <HelpText className={helpTextClassName} {...helpTextProps} status={status}>
          {(status === "danger" && meta.error) || helpText}
        </HelpText>
      }
    />
  );
}
